import { useState } from "react";

import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DefaultStatisticsCard from "layouts/views/dashboards/main/components/RecvCard";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCustomerValue } from "providers/CustomerContext";
import { useTeamValue } from "providers/TeamContext";
import { ListLayout, TableCard } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { CustomDatePicker } from "ui-components/CustomInput";
import { LoadingDialog, ExportDialog, Error_ } from "ui-components/Alert";
import { getLAHistSummary } from "services/data.report.service";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";

const cardColors = ["#bdf9f9", "#d9d9ff", "lightyellow", "#ffd9e6", "#d9ffd9", "#ffe5cc"];

const formatCbm = (value) =>
  (value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Keep "No Type" last, everything else alphabetical
const sortLocationTypes = (a, b) =>
  a === "No Type" ? 1 : b === "No Type" ? -1 : a.localeCompare(b);

const sumColumn = (table, key) =>
  table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original[key] ?? 0), 0);

function HistoricalInventorySummary() {
  const { customerOpt } = useCustomerValue();
  const { permissions, allowAll } = useTeamValue();
  const { downloadCsv, isDownloading } = useCsvDownloadHook();

  const today = dayjs().format("YYYY-MM-DD");
  const [date, setDate] = useState(dayjs().subtract(1, "day").format("YYYY-MM-DD"));
  const [alert, setAlert] = useState(null);
  const [locationTypes, setLocationTypes] = useState([]);
  const [tableData, setTableData] = useState([]);

  const exportPermission =
    allowAll ||
    (Boolean(permissions["Historical Inventory_Report"]) &&
      permissions["Historical Inventory_Report"].findIndex((b) => b === "export") !== -1);

  const { isFetching, refetch } = useQuery({
    queryKey: ["la-hist-summary", [customerOpt]],
    enabled: Boolean(customerOpt.length),
    queryFn: () => getLAHistSummary({ date, customers: customerOpt }),
    onSuccess: (data) => {
      const result = data.data.data;

      // Pivot to one row per customer with a CBM column per location type
      const types = [...new Set(result.map((r) => r.locationType))].sort(sortLocationTypes);
      const byCustomer = {};
      result.forEach((r) => {
        if (!byCustomer[r.customerCode]) {
          byCustomer[r.customerCode] = {
            customerCode: r.customerCode,
            totalLocations: 0,
            totalQuantity: 0,
            totalCubic: 0,
          };
        }
        const row = byCustomer[r.customerCode];
        row[r.locationType] = (row[r.locationType] ?? 0) + r.totalCubic;
        row.totalLocations += r.totalLocations;
        row.totalQuantity += r.totalQuantity;
        row.totalCubic += r.totalCubic;
      });

      setLocationTypes(types);
      setTableData(
        Object.values(byCustomer).sort((a, b) => a.customerCode.localeCompare(b.customerCode))
      );
    },
  });

  const cubicByType = locationTypes.map((type) => ({
    type,
    cubic: tableData.reduce((total, row) => total + (row[type] ?? 0), 0),
  }));
  const totalCubic = tableData.reduce((total, row) => total + row.totalCubic, 0);

  const columns = [
    {
      header: "Customer Code",
      accessorKey: "customerCode",
      footer: "Total",
    },
    ...locationTypes.map((type) => ({
      header: `${type} (CBM)`,
      id: `cbm-${type}`,
      accessorFn: (row) => formatCbm(row[type]),
      footer: ({ table }) => formatCbm(sumColumn(table, type)),
    })),
    {
      header: "Total Locations",
      accessorFn: (row) => row.totalLocations.toLocaleString(),
      footer: ({ table }) => sumColumn(table, "totalLocations").toLocaleString(),
    },
    {
      header: "Total Quantity",
      accessorFn: (row) => row.totalQuantity.toLocaleString(),
      footer: ({ table }) => sumColumn(table, "totalQuantity").toLocaleString(),
    },
    {
      header: "Total Storage (CBM)",
      accessorFn: (row) => formatCbm(row.totalCubic),
      footer: ({ table }) => formatCbm(sumColumn(table, "totalCubic")),
    },
  ];

  const onSearch = () => {
    setAlert(null);
    if (dayjs(today).isBefore(date)) {
      setAlert(<Error_ content="Date cannot greater than today date" onChange={setAlert} />);
      return;
    }
    refetch();
  };

  const onExport = () => {
    const exportData = tableData.map((row) => ({
      "Customer Code": row.customerCode,
      ...Object.fromEntries(locationTypes.map((type) => [`${type} (CBM)`, (row[type] ?? 0).toFixed(4)])),
      "Total Locations": row.totalLocations,
      "Total Quantity": row.totalQuantity,
      "Total Storage (CBM)": row.totalCubic.toFixed(4),
    }));
    const filename = `${dayjs(date).format("YYYYMMDD")}_HistoricalInventorySummary`;
    downloadCsv(filename, exportData, null, [
      ["Date", date],
      ...cubicByType.map(({ type, cubic }) => [`Total ${type} (CBM)`, cubic.toFixed(4)]),
      ["Total Storage (CBM)", totalCubic.toFixed(4)],
    ]);
  };

  return (
    <ListLayout lg={12}>
      {isFetching && <LoadingDialog />}
      {isDownloading && <ExportDialog />}
      {alert && <MDBox sx={{ mt: 3 }}>{alert}</MDBox>}
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <DefaultStatisticsCard
              title="Total Storage (CBM)"
              count={formatCbm(totalCubic)}
              color="#e0e0e0"
            />
          </Grid>
          {cubicByType.map(({ type, cubic }, index) => (
            <Grid item xs={12} sm={6} md={3} key={type}>
              <DefaultStatisticsCard
                title={`${type} (CBM)`}
                count={formatCbm(cubic)}
                color={cardColors[index % cardColors.length]}
              />
            </Grid>
          ))}
        </Grid>
      </MDBox>

      <TableCard>
        <Grid container justifyContent="flex-end" alignItems="center" spacing={3} sx={{ p: 2 }}>
          <Grid item xs={12} sm={4} md={3} sx={{ "& .MuiTextField-root": { mb: 0 } }}>
            <CustomDatePicker
              field={{
                label: "Date",
                name: "date",
                value: date,
                onChange: setDate,
                maxDate: dayjs(),
                disableClearable: true,
              }}
            />
          </Grid>
          <Grid item>
            <MDButton color="info" disabled={!date} onClick={onSearch}>
              search
            </MDButton>
            <MDButton
              sx={{ ml: 2 }}
              color="info"
              disabled={!exportPermission || !tableData.length}
              onClick={onExport}
            >
              export
            </MDButton>
          </Grid>
        </Grid>
        <BasicTable columns={columns} data={tableData} />
      </TableCard>
    </ListLayout>
  );
}

export default HistoricalInventorySummary;
